import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, MapPin, Clock, Video } from 'lucide-react';
import { api } from '../services/api';

const DIAS_PT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MESES_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function ymd(d) {
  return d.toISOString().slice(0, 10);
}

/**
 * Calendário público de um Coletivo.
 * - Desktop: grid mensal estilo Google Calendar
 * - Mobile (<sm): lista vertical agrupada por dia
 */
export default function ColetivoCalendar({ slug, color = '#7C3AED' }) {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [ocorrencias, setOcorrencias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [diaSel, setDiaSel] = useState(null); // YYYY-MM-DD

  const month = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    api.get(`/v2/coletivos/${slug}/calendario`, { params: { month } })
      .then((r) => setOcorrencias(r.data?.ocorrencias || []))
      .catch(() => setOcorrencias([]))
      .finally(() => setLoading(false));
  }, [slug, month]);

  // mapa: data -> lista ocorrências
  const mapa = useMemo(() => {
    const m = {};
    for (const o of ocorrencias) {
      const k = o.data;
      if (!m[k]) m[k] = [];
      m[k].push(o);
    }
    return m;
  }, [ocorrencias]);

  // dias para grid: começa no primeiro dia da semana, termina no último dia da semana do último dia
  const grid = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const start = new Date(first);
    start.setDate(start.getDate() - start.getDay()); // volta pro domingo
    const end = new Date(last);
    end.setDate(end.getDate() + (6 - end.getDay())); // avança pro sábado
    const days = [];
    const d = new Date(start);
    while (d <= end) {
      days.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    return days;
  }, [cursor]);

  function prevMonth() {
    setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
    setDiaSel(null);
  }
  function nextMonth() {
    setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
    setDiaSel(null);
  }

  const diasComEventoMobile = useMemo(() => {
    return Object.keys(mapa).sort();
  }, [mapa]);

  const todayStr = ymd(today);

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 md:p-5" data-testid="coletivo-calendar">
      {/* header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-5 h-5" style={{ color }} />
          <h2 className="text-lg font-bold">Agenda</h2>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={prevMonth}
            data-testid="cal-prev-month"
            className="p-1.5 rounded-lg hover:bg-gray-100 transition"
            aria-label="Mês anterior"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium px-2 min-w-[140px] text-center" data-testid="cal-month-label">
            {MESES_PT[cursor.getMonth()]} {cursor.getFullYear()}
          </span>
          <button
            onClick={nextMonth}
            data-testid="cal-next-month"
            className="p-1.5 rounded-lg hover:bg-gray-100 transition"
            aria-label="Próximo mês"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {loading && <div className="text-xs text-gray-500 py-2">Carregando…</div>}

      {/* DESKTOP: grade */}
      <div className="hidden sm:block">
        <div className="grid grid-cols-7 gap-1 text-xs font-medium text-gray-500 mb-1">
          {DIAS_PT.map((d) => (
            <div key={d} className="text-center py-1">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {grid.map((d) => {
            const k = ymd(d);
            const evs = mapa[k] || [];
            const outsideMonth = d.getMonth() !== cursor.getMonth();
            const isToday = k === todayStr;
            return (
              <button
                key={k}
                onClick={() => setDiaSel(k)}
                data-testid={`cal-day-${k}`}
                className={`
                  min-h-[72px] p-1.5 rounded-lg text-left transition relative border
                  ${outsideMonth ? 'bg-gray-50 text-gray-300 border-transparent' : 'bg-white border-gray-100 hover:border-purple-200'}
                  ${diaSel === k ? 'ring-2 ring-purple-500' : ''}
                `}
              >
                <div className={`text-xs font-semibold ${isToday ? 'inline-flex w-5 h-5 items-center justify-center rounded-full text-white' : ''}`}
                     style={isToday ? { backgroundColor: color } : undefined}>
                  {d.getDate()}
                </div>
                <div className="mt-1 space-y-0.5">
                  {evs.slice(0, 2).map((e, idx) => (
                    <div
                      key={`${e.evento_id}-${idx}`}
                      className="text-[10px] truncate rounded px-1 py-0.5 font-medium"
                      style={{ backgroundColor: `${color}15`, color }}
                      title={e.titulo}
                    >
                      {e.hora_inicio ? `${e.hora_inicio} ` : ''}{e.titulo}
                    </div>
                  ))}
                  {evs.length > 2 && (
                    <div className="text-[10px] text-gray-500">+{evs.length - 2} mais</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* MOBILE: lista agrupada por dia */}
      <div className="sm:hidden space-y-3">
        {diasComEventoMobile.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">Nenhum evento neste mês.</p>
        ) : (
          diasComEventoMobile.map((k) => {
            const d = new Date(k + 'T00:00:00');
            return (
              <div key={k} data-testid={`cal-mobile-day-${k}`}>
                <div className="flex items-baseline gap-2 mb-1.5">
                  <span className="text-lg font-bold" style={{ color }}>{d.getDate()}</span>
                  <span className="text-xs uppercase text-gray-500 font-medium">
                    {DIAS_PT[d.getDay()]} · {MESES_PT[d.getMonth()].slice(0, 3)}
                  </span>
                </div>
                <div className="space-y-1.5">
                  {mapa[k].map((e, idx) => (
                    <button
                      key={`${e.evento_id}-${idx}`}
                      onClick={() => setDiaSel(k)}
                      className="w-full text-left p-3 rounded-lg border border-gray-100 hover:border-purple-200 transition flex items-start gap-2"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{e.titulo}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                          {e.hora_inicio && (<span className="inline-flex items-center gap-1"><Clock className="w-3 h-3"/>{e.hora_inicio}</span>)}
                          {e.local && (<span className="inline-flex items-center gap-1 truncate"><MapPin className="w-3 h-3"/>{e.local}</span>)}
                          {e.online && (<span className="inline-flex items-center gap-1 text-blue-600"><Video className="w-3 h-3"/>Online</span>)}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* drawer/modal de detalhes do dia */}
      {diaSel && mapa[diaSel] && (
        <div className="mt-4 p-4 rounded-xl border-2 border-purple-200 bg-purple-50" data-testid="cal-day-detail">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold">Eventos em {new Date(diaSel + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' })}</h3>
            <button onClick={() => setDiaSel(null)} className="text-xs text-gray-500 hover:text-gray-800">Fechar ✕</button>
          </div>
          <div className="space-y-2">
            {mapa[diaSel].map((e, idx) => (
              <div key={`${e.evento_id}-${idx}`} className="bg-white p-3 rounded-lg border border-gray-100">
                <div className="font-medium text-sm">{e.titulo}</div>
                {e.descricao && <p className="text-xs text-gray-600 mt-1">{e.descricao}</p>}
                <div className="text-xs text-gray-500 mt-2 flex flex-wrap items-center gap-3">
                  {e.hora_inicio && (<span className="inline-flex items-center gap-1"><Clock className="w-3 h-3"/>{e.hora_inicio}{e.hora_fim && `–${e.hora_fim}`}</span>)}
                  {e.local && (<span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{e.local}</span>)}
                  {e.online && (<span className="inline-flex items-center gap-1 text-blue-600"><Video className="w-3 h-3"/>Online</span>)}
                  {e.recorrencia_tipo && e.recorrencia_tipo !== 'none' && (
                    <span className="text-purple-700 bg-purple-100 px-1.5 py-0.5 rounded text-[10px] uppercase">
                      {e.recorrencia_tipo}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
